/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useCustomSearchParams } from 'hooks/useCustomSearchParams';
import {
  calcIndexByOrder,
  removeFalsyKeys,
  calcCurrentPageWithNum,
  formatCurrency,
  formatTime,
} from 'utils';
import { Table } from 'components/table';
import { StyledActions, StyledButton } from 'styles/overrides';
import { useDeleteProduct, useProducts, usePatchProducts } from 'api/productApi';
import { ProductFilterSearchForm } from './ProductFilterSearchForm';
import { Avatar, Modal } from 'antd';
import { Notification } from 'components/Notification';
import { Link } from 'react-router-dom';
import { PATH_NAME_ACTION } from 'constants/routes';
import _ from 'lodash';
import { Publish } from 'components/common';

import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';

const Row = (props) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props['data-row-key'],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform?.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    cursor: 'move',
    ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
  };

  return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

export const ProductTable = () => {
  const intl = useIntl();
  const [search, setSearch] = useCustomSearchParams();
  const { data: product, isLoading, refetch } = useProducts({
    params: {
      page: search?.page,
      page_size: 50,
      locale_code: search?.locale_code,
      search: search?.search,
      category_id: search?.category_id
    }
  });
  const deleteProductMutation = useDeleteProduct();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // console.log(product);

  const columns = [
    {
      title: intl.formatMessage({ id: 'common.table.no' }),
      render: (text, record, index) => {
        return calcIndexByOrder({
          index,
          page: search?.page,
          limit: 50,
        });
      },
    },
    {
      title: intl.formatMessage({ id: 'views.products.table.name' }),
      dataIndex: 'name',
    },
    {
      title: intl.formatMessage({ id: 'common.table.avatar' }),
      dataIndex: ['file', 'path'],
      render: (value) => <Avatar src={value} size={32} />,
    },
    {
      title: intl.formatMessage({ id: 'views.products.table.price' }),
      dataIndex: 'price',
      render: (value) => formatCurrency(value),
    },
    {
      title: intl.formatMessage({ id: 'views.products.table.percentDiscount' }),
      dataIndex: ['percent_discount'],
    },
    {
      title: intl.formatMessage({ id: 'views.products.table.time' }),
      dataIndex: ['time'],
    },
    {
      title: intl.formatMessage({ id: 'common.table.images' }),
      dataIndex: 'files',
      render: (files) => {
        return (
          <>
            <Avatar.Group maxCount={3}>
              {_.map(files, (file) => (
                <Avatar key={file.id} src={file.path} size={32} />
              ))}
            </Avatar.Group>
          </>
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'views.products.table.categoryName' }),
      dataIndex: ['category', 'name'],
    },
    {
      title: intl.formatMessage({ id: 'common.table.published' }),
      dataIndex: 'published',
      render: (value) => <Publish published={value} />,
    },
    {
      title: intl.formatMessage({ id: 'common.table.createdAt' }),
      dataIndex: 'created_at',
      render: (value) => formatTime(value),
    },
    {
      render: ({ id }) => (
        <Link to={`${id}`}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.seeDetails' })}
          </StyledButton>
        </Link>
      ),
    },
  ];

  const handleChangeTable = ({ current: page, pageSize: limit }) => {
    setSearch(removeFalsyKeys({ ...search, page, limit }));
  };

  const handleDelete = () => {
    if (selectedRowKeys.length <= 0) return;

    Modal.confirm({
      title: intl.formatMessage({ id: 'common.modal.deleteTitle' }),
      okText: intl.formatMessage({ id: 'common.btn.confirm' }),
      onOk() {
        return deleteProductMutation.mutateAsync(selectedRowKeys, {
          onSuccess() {
            const calculatedPage = calcCurrentPageWithNum({
              page: search.page,
              limit: search.page_size,
              total: product?.meta?.total,
              num: selectedRowKeys?.length,
            });
            if (calculatedPage !== search.page) {
              setSearch(
                removeFalsyKeys({
                  ...search,
                  page: calculatedPage,
                }),
              );
            } else {
              refetch();
            }

            setSelectedRowKeys([]);
            Notification(
              'success',
              intl.formatMessage({ id: 'message.deleteSuccess' }),
            );
          },
          onError(error) {
            Notification(
              'error',
              error?.message ||
              intl.formatMessage({ id: 'message.commonError' }),
            );
          },
        });
      },
      onCancel() { },
    });
  };

  const [dataSource, setDataSource] = useState([]);
  const patchProducts = usePatchProducts();
  useEffect(() => {
    if (product?.data?.length > 0) {
      setDataSource(product?.data)
    }
  }, [product])
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    }),
  );

  const onDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      const order = dataSource?.map(item => item?.order);

      const activeIndex = dataSource.findIndex((i) => i.id === active.id);
      const overIndex = dataSource.findIndex((i) => i.id === over?.id);
      const listData = dataSource?.map(item => item);

      const result = arrayMove(listData, activeIndex, overIndex);

      setDataSource(result)
      handleUpdateTable(result, order)
    }
  };

  const handleUpdateTable = (data, order) => {
    // const pageSearch = (search?.page - 1) * 10;
    // data?.forEach((item, index) => {
    //   item['order'] = index + (pageSearch + 1)
    // })

    const id = data?.map(item => item?.id);

    const req = {
      order,
      id
    }

    patchProducts.mutate(req, {
      onSuccess(res) {
        refetch()
      },
      onError(err) {
        console.log("UPDATE FAIL!", err);
      }
    });
  }

  return (
    <>
      <ProductFilterSearchForm
        onSubmit={(values) => setSearch(values)}
        initialValues={search}
      />

      <StyledActions size={24}>
        <Link to={PATH_NAME_ACTION.CREATE}>
          <StyledButton type="primary">
            {intl.formatMessage({ id: 'common.btn.addNew' })}
          </StyledButton>
        </Link>

        <StyledButton
          disabled={selectedRowKeys.length === 0}
          onClick={handleDelete}
        >
          {intl.formatMessage({ id: 'common.btn.delete' })}
        </StyledButton>
      </StyledActions>

      <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
        <SortableContext
          items={dataSource?.map((i) => i.id)}
          strategy={verticalListSortingStrategy}
        >
          <Table
            rowKey="id"
            components={{
              body: {
                row: Row,
              },
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: (newSelectedRowKeys, newSelectedRows) => {
                setSelectedRowKeys(newSelectedRowKeys);
              },
              preserveSelectedRowKeys: true,
            }}
            columns={columns}
            dataSource={product?.data?.length > 0 ? dataSource : []}
            loading={isLoading}
            pagination={{
              current: search?.page,
              pageSize: 50,
              total: product?.meta?.total || 0,
            }}
            onChange={handleChangeTable}
          />
        </SortableContext>
      </DndContext>
    </>
  );
};
